// FindingsExport.js — client-side multi-tab Excel (.xlsx) findings export.
//
// Export: ExportFindingsButton({ customerName, slug, report, scans, settingDetails, T })
//
// Builds a six-tab workbook from report.scored_settings:
//   1. Summary       — orientation: customer, score, coverage, tier, result
//                      counts, failures by severity, failures by workload.
//   2. All Findings  — every setting regardless of result.
//   3. Action Items  — every result === "Failed", sorted by severity
//                      (Critical first) then workload alpha.
//   4. Not Assessed  — result === "Skipped" or "Error", with the platform-
//                      provided reason when present (mostly "—" today).
//   5. Passed        — completeness/reassurance tab for satisfied settings.
//   6. Cross-Mapping — captured Gold Copy framework references by raw result.
//
// Status overrides: the four findings sheets carry all five disposition columns
// (Effective Status / Override Status / Reason / Date / By). Cross-Mapping carries
// Effective Status alongside the unchanged raw scan result. The export shows what
// the customer sees — a risk-accepted Critical reads as Risk Accepted, not as a
// bare failure — while retaining the scan result for auditability.
//
// Library: xlsx-js-style (drop-in fork of SheetJS xlsx). Same API surface, but
// its writer preserves cell-level `s` styles, so the bold headers, header
// fills, and severity-cell tinting set below actually land in the .xlsx.
//
// Brand: "Patriot Consulting" / "Patriot" only; "SecureShield365" one word;
// dashboard vocabulary ("settings", "findings"). No fabricated numbers; any
// null/missing field renders as "—".

import React, { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { SEVERITY_RANK, sevKey, sevPalette, compareFindings } from "./severity";
import { getTier } from "./tiers";
import { dispositionLabel, effectiveStatusKeyOf, statusText } from "./overrideStatus";
import { baselineIsSecureScan365 } from "./regressionProvenance";
import { complexityForDisplay, priorityDifficultyFor } from "./findingRatings";
import { fmtInstant, localDateString } from "../utils/dates";

// ── Maturity tiers come from ./tiers (canonical, mirrors getLevel()). ──

// ── Severity helpers ──
// Severity ranking + colors come from ./severity (canonical, shared with the
// dashboard + PDF export). sevCellStyle below maps those hex values into the
// xlsx-js-style cell-style shape (rgb without a leading "#").

const HEADER_FILL = "0B1F3A";
const HEADER_FG = "FFFFFF";

// ── Formatters ──
const dash = (v) => {
  if (v == null) return "—";
  const s = String(v).trim();
  return s.length === 0 ? "—" : s;
};
const explanationText = (v) => {
  if (typeof v === "string") return v.trim() ? v : "—";
  return dash(v);
};
const fmtPct = (v) => (typeof v === "number" ? `${Math.round(v)}%` : "—");
// ADR-0083 weight gate: the Regressed column reports the score-affecting
// signal, not the raw regressed_from_passed flag — zero-weight rows export
// "No" even when the honest flag is set. Same rule as rec.regressed in
// SecureShield365.js and computeRegressed in ExecutiveReport.js.
// ADR-0084 provenance gate: "Yes" additionally requires the report
// envelope's collection_method === "securescan365" — absent (pre-ADR-0084
// snapshot) or anything else fails closed and the whole column exports
// "No", matching the dashboard and executive-report sites.
const regressedCell = (s, collectionMethod) =>
  collectionMethod === "securescan365" &&
  s.regressed_from_passed && (typeof s.max_possible === "number" ? s.max_possible : 0) > 0
    ? "Yes"
    : "No";
// Scan executed_at is an INSTANT — local rendering is correct. Shared
// formatter from utils/dates.js (same INSTANT vs DATE-ONLY classification
// as the dashboard).
const fmtDate = (iso) => fmtInstant(iso, { month: "long", day: "numeric", year: "numeric" });
const fmtFileDate = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return isNaN(d.getTime()) ? localDateString() : localDateString(d);
};

// ── Style helpers (emitted into cell.s; dropped by the community writer,
//    honored by xlsx-js-style). ──
const headerStyle = {
  font: { bold: true, color: { rgb: HEADER_FG } },
  fill: { patternType: "solid", fgColor: { rgb: HEADER_FILL } },
  alignment: { vertical: "center" },
};
const sectionTitleStyle = {
  font: { bold: true, sz: 12, color: { rgb: HEADER_FILL } },
};
const titleStyle = {
  font: { bold: true, sz: 14, color: { rgb: HEADER_FILL } },
};
const noteStyle = {
  font: { italic: true, color: { rgb: "5B7390" } },
};
const sevCellStyle = (sev) => {
  const p = sevPalette(sev);
  return {
    font: { bold: true, color: { rgb: p.text.replace("#", "") } },
    fill: { patternType: "solid", fgColor: { rgb: p.bg.replace("#", "") } },
    alignment: { horizontal: "center", vertical: "center" },
  };
};

// ── Status-override columns (appended to every findings sheet) ──
// Precedence, labels and the Not Assessed rule all come from ./overrideStatus,
// the same module the dashboard pill reads — the exported Effective Status and
// the on-screen pill cannot drift apart.
//
// Appended at the END of each sheet's existing columns on purpose: the raw scan
// verdict keeps its position and meaning, and the severity-tint column index
// (3) stays valid.
const OVERRIDE_HEADERS = [
  "Effective Status", "Override Status", "Override Reason", "Override Date", "Override By",
];
const OVERRIDE_COL_WIDTHS = [
  { wch: 18 }, // Effective Status
  { wch: 20 }, // Override Status
  { wch: 52 }, // Override Reason
  { wch: 20 }, // Override Date
  { wch: 28 }, // Override By
];
// `overrides` is keyed by setting_id (string keys from the API; numeric
// setting_id coerces on lookup). Override date is Dataverse `modifiedon` — an
// INSTANT, so it renders through the shared instant formatter, never a
// hand-rolled one (date-authority rule, utils/dates.js).
const overrideCells = (s, overrides) => {
  const ov = overrides?.[s.setting_id] || null;
  return [
    statusText(effectiveStatusKeyOf(s, ov)),
    ov?.override ? dispositionLabel(ov.override) : "—",
    dash(ov?.reason),
    ov?.updated_at ? fmtDate(ov.updated_at) : "—",
    dash(ov?.updated_by),
  ];
};

// These keys are snapshot data captured by scoring. A report has usable mapping
// data only when every framework key is present and each value is string/null.
// This deliberately fails closed for historical, partial, and malformed reports:
// the export must never join today's Gold Copy to an immutable scan snapshot.
const FRAMEWORK_COLUMNS = [
  ["framework_cmmc", "CMMC"],
  ["framework_cis", "CIS"],
  ["framework_hitrust", "HITRUST"],
  ["framework_iso27001", "ISO 27001"],
  ["framework_nist80053", "NIST 800-53"],
  ["framework_pcidss", "PCI DSS"],
  ["framework_soc2", "SOC2"],
];
const mappingAvailable = (s) => FRAMEWORK_COLUMNS.every(([key]) => (
  Object.prototype.hasOwnProperty.call(s, key)
  && (s[key] === null || typeof s[key] === "string")
));
const frameworkCell = (value) => (
  typeof value === "string" && value.trim().length > 0 ? value : "—"
);
const scanGroupOf = (result) => {
  if (result === "Failed") return "Open";
  if (result === "Passed") return "Satisfied";
  if (["Skipped", "Error", "NotRun", "NotApplicable"].includes(result)) return "Not assessed";
  return "Other";
};
const assessmentTypeOf = (scoringMode) => ({
  scored: "Scored", advisory: "Advisory", review: "Review",
})[scoringMode] || "Unknown";

const applyStyleToCell = (ws, addr, style) => {
  const cell = ws[addr];
  if (cell) cell.s = { ...(cell.s || {}), ...style };
};
const setLiteralTextCell = (ws, row, column, value) => {
  if (typeof value !== "string" || !value.startsWith("=")) return;
  const addr = XLSX.utils.encode_cell({ r: row, c: column });
  ws[addr] = { t: "s", v: value, s: ws[addr]?.s };
};
const applyHeaderRow = (ws, rowR, ncols) => {
  for (let c = 0; c < ncols; c++) {
    const addr = XLSX.utils.encode_cell({ r: rowR, c });
    if (ws[addr]) ws[addr].s = { ...(ws[addr].s || {}), ...headerStyle };
  }
};

// ── Sheet builders ──

function buildSummarySheet(report, scans, settings, customerName, slug) {
  const total = settings.length;
  const counts = { passed: 0, failed: 0, skipped: 0, error: 0 };
  for (const s of settings) {
    if (s.result === "Passed") counts.passed++;
    else if (s.result === "Failed") counts.failed++;
    else if (s.result === "Skipped") counts.skipped++;
    else if (s.result === "Error") counts.error++;
  }
  const failedBySev = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const s of settings) {
    if (s.result === "Failed") {
      const k = sevKey(s.severity);
      if (k in failedBySev) failedBySev[k]++;
    }
  }
  const byWorkload = new Map();
  for (const s of settings) {
    const w = (s.workload || "Unspecified").toString();
    const entry = byWorkload.get(w) || { workload: w, passed: 0, failed: 0 };
    if (s.result === "Passed") entry.passed++;
    else if (s.result === "Failed") entry.failed++;
    byWorkload.set(w, entry);
  }
  const workloadRows = Array.from(byWorkload.values())
    .filter((e) => e.passed + e.failed > 0)
    .sort((a, b) => b.failed - a.failed || (b.passed + b.failed) - (a.passed + a.failed));

  const score = report?.totals?.score_pct;
  const tierName = typeof score === "number" ? getTier(score).name : "—";
  const scanIso = report?.executed_at || report?.scored_at;

  // Row indices are tracked as the sheet is built rather than hardcoded: the
  // coverage row below is conditional, so every downstream section shifts.
  const aoa = [
    ["SecureShield365 Findings Export"],                  // 0  A1
    [""],                                                 // 1
    ["Customer", customerName || "—"],                    // 2  A3
    ["Tenant slug", slug || "—"],                         // 3  A4
    ["Scan executed", fmtDate(scanIso)],                  // 4  A5
    ["Overall Patriot Score", typeof score === "number" ? fmtPct(score) : "—"], // 5
  ];
  // Coverage line (2026-07-28 denominator decision) — the same
  // "Based on N of M settings" the dashboard prints under the score. Older
  // scored payloads predate the coverage fields; omit the row entirely there
  // rather than inventing a denominator.
  if (typeof report?.totals?.assessed_count === "number"
      && typeof report?.totals?.total_settings === "number") {
    aoa.push(["Score coverage", `Based on ${report.totals.assessed_count} of ${report.totals.total_settings} settings`]);
  }
  aoa.push(["Maturity tier", tierName]);
  aoa.push(["Scans included", Array.isArray(scans) ? scans.length : 0]);
  aoa.push([""]);

  const totalsTitleR = aoa.length;
  aoa.push(["Settings totals"]);
  const totalsHeaderR = aoa.length;
  aoa.push(["Total scored", "Passed", "Failed", "Skipped", "Error"]);
  aoa.push([total, counts.passed, counts.failed, counts.skipped, counts.error]);
  aoa.push([""]);

  const sevTitleR = aoa.length;
  aoa.push(["Failures by severity"]);
  const sevHeaderR = aoa.length;
  aoa.push(["Critical", "High", "Medium", "Low"]);
  aoa.push([failedBySev.critical, failedBySev.high, failedBySev.medium, failedBySev.low]);
  aoa.push([""]);

  const wlTitleR = aoa.length;
  aoa.push(["Failures by product"]);
  const wlHeaderR = aoa.length;
  aoa.push(["Product", "Failures", "Passed", "Total scored", "Compliance %"]);
  for (const e of workloadRows) {
    const denom = e.passed + e.failed;
    const compliance = denom > 0 ? `${Math.round((100 * e.passed) / denom)}%` : "—";
    aoa.push([e.workload, e.failed, e.passed, denom, compliance]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [
    { wch: 28 },
    { wch: 38 }, // holds the coverage sentence as well as the label values
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
  ];

  // Title + section headings (styles will activate with xlsx-js-style).
  const colA = (r) => XLSX.utils.encode_cell({ r, c: 0 });
  applyStyleToCell(ws, "A1", titleStyle);
  applyStyleToCell(ws, colA(totalsTitleR), sectionTitleStyle);
  applyStyleToCell(ws, colA(sevTitleR), sectionTitleStyle);
  applyStyleToCell(ws, colA(wlTitleR), sectionTitleStyle);
  // Header rows within each section.
  applyHeaderRow(ws, totalsHeaderR, 5);
  applyHeaderRow(ws, sevHeaderR, 4);
  applyHeaderRow(ws, wlHeaderR, 5);

  return ws;
}

function buildActionItemsSheet(settings, overrides, collectionMethod) {
  const failed = settings.filter((s) => s.result === "Failed");
  // Canonical findings order (severity rank, then score_weight desc) — matches
  // the PDF "Top Priority Findings" list via the shared compareFindings. Was
  // previously severity→workload-alpha, which diverged from the PDF.
  failed.sort(compareFindings);

  // Suggested-priority rank within this action-items set. Direction: severity
  // dominates; higher user-impact and higher complexity LOWER priority (more
  // change-management overhead, harder to land). 1 = do first.
  const lvl = (v) => { const k = (v || "").toString().toLowerCase(); return k === "high" ? 3 : k === "medium" ? 2 : k === "low" ? 1 : 2; };
  // critical=4, high=3, medium=2, low=1, informational=0, unknown=1.
  const sevWeight = (sev) => 4 - (SEVERITY_RANK[sevKey(sev)] ?? 3);
  const priorityScore = (s) => sevWeight(s.severity) * 10 - lvl(s.user_impact) - lvl(priorityDifficultyFor(s));
  // Rank by priorityScore desc → stable sequential 1-based integers per row.
  const rankByIdx = new Array(failed.length);
  failed
    .map((s, i) => ({ i, p: priorityScore(s) }))
    .sort((a, b) => b.p - a.p)
    .forEach((entry, pos) => { rankByIdx[entry.i] = pos + 1; });

  const headers = [
    "Setting ID", "Setting", "Product", "Severity", "Priority",
    "Recommended Value", "How to Fix", "Why It Matters",
    "Complexity", "User Impact", "Score Weight", "Regressed vs Scoring Predecessor",
    ...OVERRIDE_HEADERS, "Complexity Explanation", "Impact Risk Explanation",
  ];
  const rows = failed.map((s, i) => [
    s.setting_id ?? "—",
    dash(s.setting_name),
    dash(s.workload),
    dash(s.severity),
    rankByIdx[i],                                    // Priority (calculated rank, 1 = first)
    dash(s.recommended_value || s.audit_recommendation),
    Array.isArray(s.remediation_steps) && s.remediation_steps.length > 0
      ? s.remediation_steps.join(" > ")
      : "—",
    dash(s.patriot_advisor_value),
    dash(complexityForDisplay(s)),
    dash(s.user_impact),
    s.score_weight ?? "—",
    regressedCell(s, collectionMethod),
    ...overrideCells(s, overrides),
    explanationText(s.complexity_explanation),
    explanationText(s.impact_risk_explanation),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = [
    { wch: 10 }, // Setting ID
    { wch: 56 }, // Setting
    { wch: 16 }, // Workload
    { wch: 12 }, // Severity
    { wch: 9 },  // Priority
    { wch: 28 }, // Recommended Value
    { wch: 56 }, // How to Fix
    { wch: 56 }, // Why It Matters
    { wch: 12 }, // Complexity
    { wch: 14 }, // User Impact
    { wch: 12 }, // Score Weight
    { wch: 34 }, // Regressed vs Scoring Predecessor
    ...OVERRIDE_COL_WIDTHS,
    { wch: 56 }, // Complexity Explanation
    { wch: 56 }, // Impact Risk Explanation
  ];
  // NOTE: xlsx-js-style@1.2.0's writer ignores "!views" freeze (emits a bare
  // <sheetView>, no <pane>) — header freeze does NOT land in the .xlsx today.
  // Kept as intent; see buildWorkbook note for the proposed fix.
  ws["!views"] = [{ state: "frozen", ySplit: 1, topLeftCell: "A2" }];
  const lastRow = Math.max(rows.length + 1, 1);
  // Last column derived from the header count — the override columns extend it.
  ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${lastRow}` };

  applyHeaderRow(ws, 0, headers.length);
  failed.forEach((s, idx) => {
    const firstExplanationColumn = headers.length - 2;
    setLiteralTextCell(ws, idx + 1, firstExplanationColumn, explanationText(s.complexity_explanation));
    setLiteralTextCell(ws, idx + 1, firstExplanationColumn + 1, explanationText(s.impact_risk_explanation));
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: idx + 1, c: firstExplanationColumn }), { alignment: { wrapText: true, vertical: "top" } });
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: idx + 1, c: firstExplanationColumn + 1 }), { alignment: { wrapText: true, vertical: "top" } });
  });

  // Severity cell color per row (column index 3; Priority is index 4, after it).
  failed.forEach((s, idx) => {
    const sevAddr = XLSX.utils.encode_cell({ r: idx + 1, c: 3 });
    applyStyleToCell(ws, sevAddr, sevCellStyle(s.severity));
  });

  return ws;
}

function buildNotAssessedSheet(settings, overrides) {
  const notAssessed = settings.filter((s) => s.result === "Skipped" || s.result === "Error");
  notAssessed.sort((a, b) => {
    if (a.result !== b.result) return a.result.localeCompare(b.result);
    const ra = SEVERITY_RANK[sevKey(a.severity)] ?? 99;
    const rb = SEVERITY_RANK[sevKey(b.severity)] ?? 99;
    if (ra !== rb) return ra - rb;
    return (a.workload || "").localeCompare(b.workload || "");
  });

  const note = "Settings not evaluated in this assessment. Reasons populate as the platform captures them. "
    + "A row with an Override Status was dispositioned by the customer — that decision is the effective status, "
    + "and the assessment gap is recorded in the Reason column.";
  // "Status" here is the RAW scan verdict (Skipped / Error) — unchanged. The
  // effective status arrives in the appended override block, where an
  // undispositioned row reads "Not Assessed" to match the dashboard pill.
  const headers = ["Setting ID", "Setting", "Product", "Severity", "Status", "Reason", ...OVERRIDE_HEADERS];
  const rows = notAssessed.map((s) => [
    s.setting_id ?? "—",
    dash(s.setting_name),
    dash(s.workload),
    dash(s.severity),
    dash(s.result),
    dash(s.skip_reason || s.error_detail),
    ...overrideCells(s, overrides),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([[note], headers, ...rows]);
  ws["!cols"] = [
    { wch: 10 },
    { wch: 56 },
    { wch: 16 },
    { wch: 12 },
    { wch: 12 },
    { wch: 60 },
    ...OVERRIDE_COL_WIDTHS,
  ];
  // Merge the note across the full header width.
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
  // Freeze the note + header so both stay visible while scrolling.
  ws["!views"] = [{ state: "frozen", ySplit: 2, topLeftCell: "A3" }];
  const lastRow = rows.length + 2;
  ws["!autofilter"] = { ref: `A2:${XLSX.utils.encode_col(headers.length - 1)}${lastRow}` };

  applyStyleToCell(ws, "A1", noteStyle);
  applyHeaderRow(ws, 1, headers.length);

  // Severity cell color per row (column index 3, starting at sheet row 2).
  notAssessed.forEach((s, idx) => {
    const sevAddr = XLSX.utils.encode_cell({ r: idx + 2, c: 3 });
    applyStyleToCell(ws, sevAddr, sevCellStyle(s.severity));
  });

  return ws;
}

function buildPassedSheet(settings, overrides) {
  const passed = settings.filter((s) => s.result === "Passed");
  passed.sort((a, b) => {
    const w = (a.workload || "").localeCompare(b.workload || "");
    if (w !== 0) return w;
    return (a.setting_id || 0) - (b.setting_id || 0);
  });

  // Override columns carry here too: a passing setting can still hold an old
  // disposition (the drawer surfaces exactly this as a stale override to
  // clear), and the export must not be the one place it goes invisible.
  const headers = ["Setting ID", "Setting", "Product", "Severity", "Recommended Value", ...OVERRIDE_HEADERS];
  const rows = passed.map((s) => [
    s.setting_id ?? "—",
    dash(s.setting_name),
    dash(s.workload),
    dash(s.severity),
    dash(s.recommended_value || s.audit_recommendation),
    ...overrideCells(s, overrides),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = [
    { wch: 10 },
    { wch: 56 },
    { wch: 16 },
    { wch: 12 },
    { wch: 28 },
    ...OVERRIDE_COL_WIDTHS,
  ];
  ws["!views"] = [{ state: "frozen", ySplit: 1, topLeftCell: "A2" }];
  const lastRow = Math.max(rows.length + 1, 1);
  ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${lastRow}` };

  applyHeaderRow(ws, 0, headers.length);

  // Severity cell color per row.
  passed.forEach((s, idx) => {
    const sevAddr = XLSX.utils.encode_cell({ r: idx + 1, c: 3 });
    applyStyleToCell(ws, sevAddr, sevCellStyle(s.severity));
  });

  return ws;
}

function buildCrossMappingSheet(settings, overrides) {
  const orderedGroups = ["Open", "Satisfied", "Not assessed", "Other"];
  const groupRank = new Map(orderedGroups.map((group, index) => [group, index]));
  const rowsForSettings = [...settings].sort((a, b) => {
    const aGroup = scanGroupOf(a.result);
    const bGroup = scanGroupOf(b.result);
    const rank = groupRank.get(aGroup) - groupRank.get(bGroup);
    if (rank !== 0) return rank;
    if (aGroup === "Open") {
      const findingOrder = compareFindings(a, b);
      if (findingOrder !== 0) return findingOrder;
    }
    const productOrder = dash(a.workload).localeCompare(dash(b.workload));
    if (productOrder !== 0) return productOrder;
    return (a.setting_id ?? Number.MAX_SAFE_INTEGER) - (b.setting_id ?? Number.MAX_SAFE_INTEGER);
  });
  const frameworkSummary = FRAMEWORK_COLUMNS.map(([key, label]) => [
    label,
    ...orderedGroups.map((group) => rowsForSettings.filter((setting) => (
      scanGroupOf(setting.result) === group
      && mappingAvailable(setting)
      && typeof setting[key] === "string"
      && setting[key].trim().length > 0
    )).length),
  ]);
  const unavailableRowCount = rowsForSettings.filter((setting) => !mappingAvailable(setting)).length;
  const noFrameworkKeysInReport = rowsForSettings.length > 0 && rowsForSettings.every((setting) => (
    FRAMEWORK_COLUMNS.every(([key]) => !Object.prototype.hasOwnProperty.call(setting, key))
  ));

  const statusNote = "Settings are grouped by the results of the scan in this report. Effective Status shows the status currently applied to each setting. ";
  const note = noFrameworkKeysInReport
    ? statusNote + "Framework mappings were not captured in this report."
    : statusNote
      + "Framework references reflect the mappings available when this report was created. "
      + "These are reference mappings, not a determination of framework compliance. "
      + "Rows marked Mapping unavailable in this report do not have a complete framework mapping.";
  const headers = [
    "Setting ID", "Setting", "Product", "Severity", "Scan group", "Scan result",
    "Assessment type", "Effective Status", "Mapping availability",
    ...FRAMEWORK_COLUMNS.map(([, label]) => label),
  ];
  const tableRows = rowsForSettings.map((setting) => {
    const available = mappingAvailable(setting);
    return [
      setting.setting_id ?? "—",
      dash(setting.setting_name),
      dash(setting.workload),
      dash(setting.severity),
      scanGroupOf(setting.result),
      dash(setting.result),
      assessmentTypeOf(setting.scoring_mode),
      statusText(effectiveStatusKeyOf(setting, overrides?.[setting.setting_id] || null)),
      available ? "Available" : "Mapping unavailable in this report",
      ...FRAMEWORK_COLUMNS.map(([key]) => available ? frameworkCell(setting[key]) : "—"),
    ];
  });
  const aoa = [
    ["SecureShield365 Compliance Cross-Mapping"],
    [note],
    [],
    ["Framework", ...orderedGroups],
    ...frameworkSummary,
    ["Mapping unavailable report rows", unavailableRowCount],
    [],
    headers,
    ...tableRows,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
  ];
  ws["!cols"] = [
    { wch: 10 }, { wch: 56 }, { wch: 16 }, { wch: 12 }, { wch: 15 },
    { wch: 14 }, { wch: 16 }, { wch: 22 }, { wch: 34 },
    ...FRAMEWORK_COLUMNS.map(() => ({ wch: 34 })),
  ];
  ws["!rows"] = [];
  ws["!rows"][1] = { hpt: 45 };
  const headerRowIndex = 13;
  const headerRow = headerRowIndex + 1;
  const lastRow = headerRow + tableRows.length;
  ws["!autofilter"] = { ref: `A${headerRow}:${XLSX.utils.encode_col(headers.length - 1)}${lastRow}` };
  applyStyleToCell(ws, "A1", titleStyle);
  applyStyleToCell(ws, "A2", noteStyle);
  applyStyleToCell(ws, "A2", { alignment: { wrapText: true, vertical: "top" } });
  applyHeaderRow(ws, 3, orderedGroups.length + 1);
  applyHeaderRow(ws, headerRowIndex, headers.length);
  tableRows.forEach((row, index) => {
    const sheetRow = headerRowIndex + 1 + index;
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: sheetRow, c: 1 }), { alignment: { wrapText: true, vertical: "top" } });
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: sheetRow, c: 3 }), sevCellStyle(rowsForSettings[index].severity));
    FRAMEWORK_COLUMNS.forEach((_, frameworkIndex) => {
      const column = 9 + frameworkIndex;
      setLiteralTextCell(ws, sheetRow, column, row[column]);
      applyStyleToCell(ws, XLSX.utils.encode_cell({ r: sheetRow, c: column }), { alignment: { wrapText: true, vertical: "top" } });
    });
  });
  return ws;
}

// All Findings — single-source sheet: every setting regardless of result.
function buildAllFindingsSheet(settings, overrides, collectionMethod) {
  const all = [...settings].sort((a, b) => {
    const ra = SEVERITY_RANK[sevKey(a.severity)] ?? 99;
    const rb = SEVERITY_RANK[sevKey(b.severity)] ?? 99;
    if (ra !== rb) return ra - rb;
    const w = (a.workload || "").localeCompare(b.workload || "");
    if (w !== 0) return w;
    return (a.setting_id || 0) - (b.setting_id || 0);
  });

  // "Result" is the raw scan verdict and stays exactly as scored — the
  // appended Effective Status is additive, never a replacement.
  const headers = [
    "Setting ID", "Setting", "Product", "Severity", "Result",
    "Recommended Value", "Your Value", "How to Fix", "Why It Matters",
    "Complexity", "User Impact", "Score Weight", "Regressed vs Scoring Predecessor", "Skip/Error Reason",
    ...OVERRIDE_HEADERS, "Complexity Explanation", "Impact Risk Explanation",
  ];
  const rows = all.map((s) => [
    s.setting_id ?? "—",
    dash(s.setting_name),
    dash(s.workload),
    dash(s.severity),
    dash(s.result),
    dash(s.recommended_value || s.audit_recommendation),
    dash(s.actual_value),                            // Your Value
    Array.isArray(s.remediation_steps) && s.remediation_steps.length > 0
      ? s.remediation_steps.join(" > ")
      : "—",
    dash(s.patriot_advisor_value),
    dash(complexityForDisplay(s)),
    dash(s.user_impact),
    s.score_weight ?? "—",
    regressedCell(s, collectionMethod),
    dash(s.skip_reason || s.error_detail),           // Skip/Error Reason
    ...overrideCells(s, overrides),
    explanationText(s.complexity_explanation),
    explanationText(s.impact_risk_explanation),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = [
    { wch: 10 }, // Setting ID
    { wch: 56 }, // Setting
    { wch: 16 }, // Workload
    { wch: 12 }, // Severity
    { wch: 12 }, // Result
    { wch: 28 }, // Recommended Value
    { wch: 28 }, // Your Value
    { wch: 56 }, // How to Fix
    { wch: 56 }, // Why It Matters
    { wch: 12 }, // Complexity
    { wch: 14 }, // User Impact
    { wch: 12 }, // Score Weight
    { wch: 34 }, // Regressed vs Scoring Predecessor
    { wch: 40 }, // Skip/Error Reason
    ...OVERRIDE_COL_WIDTHS,
    { wch: 56 }, // Complexity Explanation
    { wch: 56 }, // Impact Risk Explanation
  ];
  // NOTE: "!views" freeze is a no-op in xlsx-js-style@1.2.0 (see buildWorkbook).
  ws["!views"] = [{ state: "frozen", ySplit: 1, topLeftCell: "A2" }];
  const lastRow = Math.max(rows.length + 1, 1);
  ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${lastRow}` };

  applyHeaderRow(ws, 0, headers.length);
  all.forEach((s, idx) => {
    const firstExplanationColumn = headers.length - 2;
    setLiteralTextCell(ws, idx + 1, firstExplanationColumn, explanationText(s.complexity_explanation));
    setLiteralTextCell(ws, idx + 1, firstExplanationColumn + 1, explanationText(s.impact_risk_explanation));
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: idx + 1, c: firstExplanationColumn }), { alignment: { wrapText: true, vertical: "top" } });
    applyStyleToCell(ws, XLSX.utils.encode_cell({ r: idx + 1, c: firstExplanationColumn + 1 }), { alignment: { wrapText: true, vertical: "top" } });
  });

  // Severity cell color per row (column index 3).
  all.forEach((s, idx) => {
    const sevAddr = XLSX.utils.encode_cell({ r: idx + 1, c: 3 });
    applyStyleToCell(ws, sevAddr, sevCellStyle(s.severity));
  });

  return ws;
}

// ── Workbook orchestration ──
function buildWorkbook({ report, scans, customerName, slug, settingDetails }) {
  const settings = Array.isArray(report?.scored_settings) ? report.scored_settings : [];
  // Same disposition source (and same fallback) as the PDF export: the live
  // `settingDetails` the dashboard holds — so an override saved without a
  // report refetch exports correctly — falling back to the `report.overrides`
  // the API shipped with the scan. No additional fetch either way.
  const overrides = settingDetails || report?.overrides || {};
  const wb = XLSX.utils.book_new();
  // FREEZE-PANE LIMITATION: xlsx-js-style@1.2.0's XLSX writer does not serialize
  // freeze panes — verified empirically: "!views"/"!freeze"/"!panes" all emit a
  // bare <sheetView workbookViewId="0"/> with no <pane>. The community SheetJS
  // writer treats pane writing as a Pro-only feature. Sheets keep "!views" as
  // intent (functional under SheetJS Pro). To actually freeze today, post-process
  // the written buffer: unzip, inject <pane ySplit=".." topLeftCell=".." state="frozen"/>
  // into each xl/worksheets/sheetN.xml <sheetView>, rezip — needs a zip dep (e.g.
  // fflate/JSZip). Out of scope for this pass; autofilter still works.
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(report, scans, settings, customerName, slug), "Summary");
  // ADR-0084: the envelope's collection_method gates the Regressed column.
  // Amendment (2026-08-08): the BASELINE must be SecureScan365 too — passing
  // undefined when the scan-history join fails closed makes regressedCell
  // export "No" everywhere, exactly like a pre-ADR-0084 report.
  const methodForRegressedColumn = baselineIsSecureScan365(report, scans)
    ? report?.collection_method
    : undefined;
  XLSX.utils.book_append_sheet(wb, buildAllFindingsSheet(settings, overrides, methodForRegressedColumn), "All Findings");
  XLSX.utils.book_append_sheet(wb, buildActionItemsSheet(settings, overrides, methodForRegressedColumn), "Action Items");
  XLSX.utils.book_append_sheet(wb, buildNotAssessedSheet(settings, overrides), "Not Assessed");
  XLSX.utils.book_append_sheet(wb, buildPassedSheet(settings, overrides), "Passed");
  XLSX.utils.book_append_sheet(wb, buildCrossMappingSheet(settings, overrides), "Cross-Mapping");
  // Workbook properties — set so Excel's File > Info shows the source.
  wb.Props = {
    Title: `${customerName || "Client"} — SecureShield365 Findings`,
    Author: "Patriot Consulting",
    Subject: "SecureShield365 Findings Export",
    Company: "Patriot Consulting",
  };
  return wb;
}

// ── Button ──
export const ExportFindingsButton = ({ customerName, slug, report, scans, settingDetails, T, onProductAnalyticsAction }) => {
  const [busy, setBusy] = useState(false);

  const hasData = Array.isArray(report?.scored_settings) && report.scored_settings.length > 0;

  // Mirrors the scan-dropdown button styling in SecureShield365.js (also used
  // by DownloadReportButton) so the two exports sit visually together.
  const baseStyle = {
    fontSize: 11,
    color: T?.muted || "#a8bddc",
    padding: "4px 10px",
    borderRadius: 6,
    border: `1px solid ${T?.border || "rgba(255,255,255,0.08)"}`,
    background: "rgba(255,255,255,0.02)",
    fontFamily: "inherit",
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    lineHeight: 1,
  };

  if (!hasData) {
    return (
      <span
        style={{ ...baseStyle, opacity: 0.4, cursor: "not-allowed" }}
        title="Export unavailable — no scored settings on this scan"
      >
        Export findings
      </span>
    );
  }

  const handleClick = () => {
    if (busy) return;
    try { onProductAnalyticsAction?.("findings_export_requested"); } catch { /* observation must not alter export */ }
    setBusy(true);
    // Defer the heavy work so the button can repaint to its busy state first.
    setTimeout(() => {
      try {
        const wb = buildWorkbook({ report, scans, customerName, slug, settingDetails });
        const scanIso = report?.executed_at || report?.scored_at;
        const filename = `${slug || "client"}-SecureShield365-Findings-${fmtFileDate(scanIso)}.xlsx`;
        XLSX.writeFile(wb, filename);
      } finally {
        setBusy(false);
      }
    }, 0);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      style={{
        ...baseStyle,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? "Preparing…" : "Export findings"}
    </button>
  );
};

export default ExportFindingsButton;
